import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';

const About = () => {
  const navigate = useNavigate();

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const values = [
    {
      title: "Precision",
      description: "Every project is handled with meticulous attention to detail and precise application techniques."
    },
    {
      title: "Durability",
      description: "We focus on long-lasting results that stand the test of time and environmental conditions."
    },
    {
      title: "Expertise",
      description: "Specialized knowledge across interior, exterior, decorative, and industrial coating applications."
    }
  ];

  return (
    <div className="pt-24 min-h-screen bg-accent-cream">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #8B7355 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl font-serif text-navy-900 mb-6 leading-tight"
              variants={fadeInUp}
            >
              About <span className="text-primary-brown">CraftCoat</span> Solutions
            </motion.h1>

            <motion.div
              className="w-24 h-1 bg-primary-brown mx-auto mb-8"
              variants={fadeInUp}
            ></motion.div>

            <motion.p
              className="text-xl md:text-2xl text-primary-dark-brown leading-relaxed font-light"
              variants={fadeInUp}
            >
              Surface finishing and coating services specializing in high-quality paint
              and protective applications for both functional and aesthetic needs.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-5xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            {/* Introduction */}
            <motion.div className="mb-16" variants={fadeInUp}>
              <div className="bg-accent-cream rounded-2xl p-8 md:p-12 shadow-lg">
                <h2 className="text-3xl md:text-4xl font-serif text-navy-900 mb-6">
                  Our Approach
                </h2>
                <p className="text-lg text-primary-dark-brown leading-relaxed mb-4">
                  We work closely with clients to understand the environment, material requirements,
                  and design expectations before developing tailored solutions.
                </p>
                <p className="text-lg text-primary-dark-brown leading-relaxed">
                  Our expertise covers interior and exterior painting, decorative finishes,
                  industrial coatings, and premium floor treatments. Whether it's enhancing
                  a residential space, refining commercial interiors, or protecting high-performance
                  industrial surfaces, we focus on durability, consistency, and clean execution.
                </p>
              </div>
            </motion.div>

            {/* Core Values Grid */}
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-serif text-navy-900 mb-10 text-center">
                What Drives Us
              </h2>

              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {values.map((value, index) => (
                  <Card key={index} delay={index * 0.1}>
                    <Card.Content className="text-center p-8">
                      <div className="w-16 h-16 bg-primary-brown rounded-full flex items-center justify-center mx-auto mb-6">
                        <div className="w-8 h-8 border-2 border-white rounded-full"></div>
                      </div>
                      <Card.Title className="text-2xl mb-4">
                        {value.title}
                      </Card.Title>
                      <Card.Description>
                        {value.description}
                      </Card.Description>
                    </Card.Content>
                  </Card>
                ))}
              </div>
            </motion.div>

            {/* Commitment Statement */}
            <motion.div
              className="bg-gradient-to-br from-navy-900 to-navy-800 rounded-2xl p-8 md:p-12 text-center shadow-2xl"
              variants={fadeInUp}
            >
              <h2 className="text-3xl md:text-4xl font-serif text-accent-cream mb-6">
                Our Commitment
              </h2>
              <p className="text-lg md:text-xl text-accent-cream leading-relaxed font-light max-w-3xl mx-auto">
                Every project is handled with attention to detail, accurate surface preparation & execution,
                and a commitment to long-lasting results. Our goal is simple: finishes
                that look refined and perform reliably.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Bottom Decorative Section */}
      <section className="py-16 bg-accent-cream">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h3 className="text-2xl md:text-3xl font-serif text-primary-brown mb-4">
              Ready to Transform Your Space?
            </h3>
            <p className="text-lg text-primary-dark-brown mb-8">
              Let's discuss how we can bring your vision to life with quality finishes that last.
            </p>
            <motion.button
              onClick={() => navigate('/contact')}
              className="inline-block bg-primary-brown text-white px-8 py-4 rounded-lg font-medium text-lg hover:bg-primary-dark-brown transition-colors shadow-lg cursor-pointer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get in Touch
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;